import React, { memo, useState } from 'react';
import { useMutation } from 'react-query';
import { ToastContainer, toast } from 'react-toastify';
import dayjs from 'dayjs';
import { createStyles, makeStyles, withStyles, } from '@material-ui/core/styles';
import Slider from '@material-ui/core/Slider';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';

import { CustomInput, DatePicker, ModalForm } from 'components';
import { isNameValid, isPhoneValid, } from 'src/utils/validation';

import { basePostRequest } from 'utils/api/api';
import { questReviewUrl, saveClientUrl } from 'utils/api/requestUrls'

const styles = makeStyles(() => createStyles({
  input: {
    '& label': {
      color: '#805FAA',
    },
    '&:hover label': {
      color: 'white',
    },
    '& label.Mui-focused': {
      color: 'white',
    },
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': {
        transition: '0.3s',
        borderColor: '#805FAA',
        borderWidth: '1px',
        borderRadius: '10px',
      },
      '&:hover fieldset': {
        borderColor: '#CAC6CE',
        borderWidth: '1px',
        borderRadius: '10px',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#CAC6CE',
        borderWidth: '1px',
        borderRadius: '10px',
      },
    },
  },
}))

const PrettoSlider = withStyles({
  root: {
    height: 8,
  },
  thumb: {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    marginTop: -7,
    marginLeft: -12,
    '&:focus, &:hover, &$active': {
      boxShadow: 'inherit',
    },
  },
  active: {},
  valueLabel: {
    left: 'calc(-50% + 8px)',
    top: -20,
    '& *': {
      background: 'transparent',
      color: '#ffffff',
    },
  },
  track: {
    height: 11,
    borderRadius: 4,
  },
  rail: {
    backgroundColor: '#626161',
    border: '1px solid currentColor',
    height: 11,
    borderRadius: 4,
  },
  colorPrimary: {
    color: '#f3bc35',
  },
  colorSecondary: {
    color: '#F00C3C',
  },
})(Slider,)

interface GiveModalProps {
  isOpen: boolean,
  setOpen: any,
  title: string,
  quizId: string,
  setIsSuccessReview: any,
}

export default memo(function ReviewModal(props: GiveModalProps,) {
  const pageView = (event) => {
    //@ts-ignore
    window && window.dataLayer && window.dataLayer.push({
      'event': event,
    });
  }

  const classes = styles()

  const invalidVerificationCode = (text: string) => toast.error(text, {
    position: toast.POSITION.TOP_CENTER
  });

  const [
    formData, setFormData,
  ] = useState({
    overallRating: 10,
    overallImpression: 10,
    scriptRating: 10,
    staffRating: 10,
    atmosphereRating: 10,
    date: dayjs().format('YYYY-MM-DD',),
    phone: '',
    name: '',
    reviewText: '',
  })

  const handleFieldsChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const {
    name,
    phone,
    reviewText,
  } = formData

  const [
    errorText, setErrorText,
  ] = useState<string>('',)

  const {
    isOpen,
    setOpen,
    title,
    quizId,
    setIsSuccessReview,
  } = props

  const { mutate: sendReview, isLoading: sendReviewLoading } =
    useMutation((body: any) => basePostRequest(questReviewUrl, body), {
      onSuccess: (res) => {
        pageView('review_form_send');
        setIsSuccessReview(true);
        setOpen(false);
      },
      onError: (err) => {
        invalidVerificationCode('Oooops! Серверная ошибка!');
      },
    });

  const { mutate: saveClientSubmit, isLoading: saveClientLoading } =
    useMutation(() => basePostRequest(saveClientUrl, { name: name, phoneNumber: phone }), {
      onSuccess: (res) => {
        const payload = {
          client: res,
          accepted: false,
          actorsRating: formData.staffRating,
          atmosphereRating: formData.atmosphereRating,
          generalImpressionRating: formData.overallImpression,
          id: null,
          overallRating: formData.overallRating,
          plotAndTaskRating: formData.scriptRating,
          quest: {
            id: quizId,
          },
          review: formData.reviewText,
          visitDate: formData.date,
        };

        if (!!payload) {
          sendReview(payload);
        }
      },
      onError: (err) => {
        console.warn('error', err);
      },
    });

  const handleSliderChange = (name,) => (event, value,) => {
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  }

  const handleReviewInputBlur = (e,) => {
    if (e.target.value === '' || e.target.value === null) {
      setErrorText('Поле не должно оставаться пустым',)
    }
  }

  const handleSubmit = () => {
    if (isPhoneValid(formData.phone) && isNameValid(formData.name) && !!formData.reviewText) {
      saveClientSubmit()
    } else {
      invalidVerificationCode('Проверьте данные!');
    }
  }

  return (
    <div>
      <ModalForm {...{
        modalTitle: title,
        isOpen,
        setOpen,
        onSubmit: handleSubmit,
        isFormInvalid: false,
        submitButtonLabel: 'Отправить отзыв',
        modalWidth: 'md',
        isShowPrivacyPolice: true,
      }}>
        <ToastContainer />
        <div className={'flex flex-shrink-0 flex-row md:flex-col sm:flex-col justify-between w-full mb-3'}>
          <CustomInput {...{
            type: 'name',
            label: 'Имя',
            name: 'name',
            isValid: isNameValid,
            helperText: 'Поле не должно оставаться пустым',
            onChange: handleFieldsChange,
          }} />
          <CustomInput {...{
            type: 'phone',
            label: 'Телефон',
            name: 'phone',
            isValid: isPhoneValid,
            helperText: 'Неверный формат. Пример +375291234567',
            onChange: handleFieldsChange,
          }} />
      
        </div>
        <DatePicker {...{
            label: 'Дата посещения',
            isInvalid: false,
            setIsInvalid: () => console.log('123'),
            setPickerData: (value,) => setFormData(prevState => ({ ...prevState, date: value })),
          }
          } />
        <div className={'flex flex-shrink-0 flex-col mt-6 w-full'}>
          <div className={'flex flex-shrink-0 flex-row md:flex-col sm:flex-col items-center mt-4'}>
            <div className={'w-1/4 md:w-full sm:w-full md:text-center sm:text-center mr-2 md:mr-0 md:mb-6 sm:mr-0 sm:mb-6'}>
              Общий рейтинг
            </div>
            <div className={'w-3/4 md:w-full sm:w-full'}>
              <PrettoSlider onChange={handleSliderChange('overallRating',)} color={'primary'} valueLabelDisplay={'on'} aria-label={'slider'} max={10} min={0} defaultValue={10} />
            </div>
          </div>
          {quizId && <><div className={'flex flex-shrink-0 flex-row md:flex-col sm:flex-col items-center mt-4'}>
            <div className={'w-1/4 md:w-full sm:w-full md:text-center sm:text-center mr-2 md:mr-0 md:mb-6 sm:mr-0 sm:mb-6'}>
              Общее впечатление
            </div>
            <div className={'w-3/4 md:w-full sm:w-full'}>
              <PrettoSlider onChange={handleSliderChange('overallImpression',)} color={'secondary'} valueLabelDisplay={'on'} aria-label={'slider'} max={10} min={0} defaultValue={10} />
            </div>
          </div>
            <div className={'flex flex-row md:flex-col sm:flex-col items-center mt-4'}>
              <div className={'w-1/4 md:w-full sm:w-full md:text-center sm:text-center mr-2 md:mr-0 md:mb-6 sm:mr-0 sm:mb-6'}>
                Cюжет и задания
              </div>
              <div className={'w-3/4 md:w-full sm:w-full'}>
                <PrettoSlider onChange={handleSliderChange('scriptRating',)} color={'secondary'} valueLabelDisplay={'on'} aria-label={'slider'} max={10} min={0} defaultValue={10} />
              </div>
            </div>
            <div className={'flex flex-row md:flex-col sm:flex-col items-center mt-4'}>
              <div className={'w-1/4 md:w-full sm:w-full md:text-center sm:text-center mr-2 md:mr-0 md:mb-6 sm:mr-0 sm:mb-6'}>
                Персонал
              </div>
              <div className={'w-3/4 md:w-full sm:w-full'}>
                <PrettoSlider onChange={handleSliderChange('staffRating',)} color={'secondary'} valueLabelDisplay={'on'} aria-label={'slider'} max={10} min={0} defaultValue={10} />
              </div>
            </div>
            <div className={'flex flex-row md:flex-col sm:flex-col items-center mt-4'}>
              <div className={'w-1/4 md:w-full sm:w-full md:text-center sm:text-center mr-2 md:mr-0 md:mb-6 sm:mr-0 sm:mb-6 leading-6 top-2'}>
                Атмосфера
              </div>
              <div className={'w-3/4 md:w-full sm:w-full'}>
                <PrettoSlider onChange={handleSliderChange('atmosphereRating',)} color={'secondary'} valueLabelDisplay={'on'} aria-label={'slider'} max={10} min={0} defaultValue={10} />
              </div>
            </div> </>}
        </div>
        <div className={'mt-4 mb-4 w-full'}>
          <FormControl className={'w-full mt-4'}>
            <TextField
              onBlur={handleReviewInputBlur}
              className={`${classes.input} flex-shrink-0`}
              label="Отзыв"
              name={'reviewText'}
              onChange={handleFieldsChange}
              multiline
              rows={7}
              helperText={errorText}
              // error={reviewError}
              variant="outlined"
            />
          </FormControl>
        </div>
        {/* <div className={'flex justify-center mt-4 w-full'}> */}
        {/*  <div className={'w-3/5 md:w-full'}> */}
        {/*    <Button {...{ */}
        {/*      colorName: 'secondary', */}
        {/*      disabled: false, */}
        {/*    }}> */}
        {/*    Загрузить изображение */}
        {/*    </Button> */}
        {/*  </div> */}
        {/* </div> */}
      </ModalForm>
    </div>
  )
})
